import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Plus, Trash2 } from 'lucide-react';
import { type TaskView } from '@apm/contracts';
import { errorMessage } from '../../../lib/error-message';
import { boardApi, boardKeys } from '../../board/api';
import { ganttApi, ganttKeys } from '../../gantt/api';

/**
 * Việc phải xong trước — APM-TASK-g.
 *
 * ## Vì sao khối này lưu NGAY, không chờ nút Lưu của form
 *
 * Mọi trường khác của form gom lại thành **một** lời gọi `PATCH /tasks/:id`.
 * Phụ thuộc thì không: mỗi cạnh là một hàng riêng với endpoint riêng
 * (`POST`/`DELETE /dependencies`), và server chặn chu trình **lúc thêm**. Gom
 * chúng vào nút Lưu nghĩa là người dùng thêm ba cạnh, bấm Lưu, rồi nhận một lỗi
 * chu trình cho cạnh thứ hai — mà lúc đó cạnh một và ba đã ghi hay chưa thì
 * không ai nói được.
 *
 * Lưu ngay thì mỗi thao tác có phản hồi của riêng nó, và một cạnh bị từ chối
 * không kéo theo cạnh nào khác.
 *
 * ## ⚠️ Danh sách chọn bỏ những việc chắc chắn bị từ chối
 *
 * Chính nó (tự phụ thuộc vào mình), và những việc đã là tiền nhiệm. Server từ
 * chối cả hai, nhưng mời người dùng chọn một thứ chắc chắn hỏng là mời họ nhận
 * một thông báo lỗi thay cho một câu trả lời.
 *
 * Chu trình sâu hơn (A→B→C→A) thì **không** lọc ở đây: giao diện sẽ phải dựng
 * lại cả đồ thị và chạy lại đúng thuật toán của server, và hai bản cài đặt của
 * cùng một luật là hai chỗ để chúng trôi khỏi nhau. Server là nơi quyết định,
 * và câu lỗi của nó nói rõ vì sao.
 */
export function TaskDependencyEditor({
  projectId,
  task,
}: {
  projectId: string;
  task: TaskView;
}): ReactNode {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [picked, setPicked] = useState('');

  /*
    Hai nguồn đã có sẵn, không thêm endpoint nào:

    - Gantt view mang `links` — chính là các cạnh phụ thuộc, và nó là dữ liệu
      mà biểu đồ đang vẽ mũi tên từ đó.
    - Board với `all = true` mang **mọi** task của dự án, kể cả việc chưa xếp
      lịch. `GanttView.bars` thì không: việc chưa có ngày không có thanh, mà nối
      phụ thuộc cho một việc chưa xếp lịch là chuyện hoàn toàn hợp lệ.
  */
  const gantt = useQuery({
    queryKey: ganttKeys.view(projectId),
    queryFn: () => ganttApi.view(projectId),
  });

  const board = useQuery({
    queryKey: boardKeys.board(projectId, 'all'),
    queryFn: () => boardApi.board(projectId, undefined, true),
  });

  /*
    Nạp lại **cả** Gantt: thêm một cạnh đổi cả đường găng lẫn `float` của mọi
    task phía sau, không chỉ đổi danh sách mũi tên (xem chú thích ở `ganttApi`).
  */
  const refresh = (): void =>
    void queryClient.invalidateQueries({ queryKey: ganttKeys.view(projectId) });

  const add = useMutation({
    mutationFn: (fromTaskId: string) => ganttApi.addDependency(projectId, fromTaskId, task.id),
    onSuccess: () => {
      setPicked('');
      refresh();
    },
    onError: (error) => toast.error(errorMessage(error, t)),
  });

  const remove = useMutation({
    mutationFn: (dependencyId: string) => ganttApi.removeDependency(dependencyId),
    onSuccess: refresh,
    onError: (error) => toast.error(errorMessage(error, t)),
  });

  const all = (board.data?.columns ?? []).flatMap((column) => column.tasks);
  const byId = new Map(all.map((row) => [row.id, row]));

  /** Cạnh trỏ **vào** task này: "việc kia phải xong trước việc này". */
  const incoming = (gantt.data?.links ?? []).filter((edge) => edge.toTaskId === task.id);
  const alreadyLinked = new Set(incoming.map((edge) => edge.fromTaskId));

  const candidates = all.filter((row) => row.id !== task.id && !alreadyLinked.has(row.id));

  return (
    <section className="flex flex-col gap-2 border-t border-input p-3">
      <h4 className="text-sm font-medium">{t('dependency.title')}</h4>
      <p className="text-xs text-muted-foreground">{t('dependency.help')}</p>

      {incoming.length === 0 ? (
        <p className="text-xs text-muted-foreground">{t('dependency.none')}</p>
      ) : (
        <ul className="flex flex-col gap-1">
          {incoming.map((edge) => {
            const from = byId.get(edge.fromTaskId);
            return (
              <li key={edge.id} className="flex items-center gap-2 text-sm">
                <span className="min-w-0 flex-1 truncate">
                  {/*
                    Việc thuộc dự án khác không có trong danh sách của dự án này
                    — server không cho nối xuyên dự án, nhưng dữ liệu cũ có thể
                    còn. Hiện mã rút gọn thay vì một dòng trống.
                  */}
                  <span className="font-mono text-xs text-muted-foreground">
                    {from?.key ?? edge.fromTaskId.slice(0, 8)}
                  </span>{' '}
                  {from?.title ?? ''}
                </span>
                <button
                  type="button"
                  aria-label={t('dependency.removeOne', { key: from?.key ?? '' })}
                  disabled={remove.isPending}
                  onClick={() => remove.mutate(edge.id)}
                  className="p-1 text-muted-foreground hover:text-destructive rounded-full glass-hover"
                >
                  <Trash2 className="size-4" aria-hidden />
                </button>
              </li>
            );
          })}
        </ul>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <select
          aria-label={t('dependency.pick')}
          value={picked}
          onChange={(event) => setPicked(event.target.value)}
          className="h-8 min-w-0 flex-1 rounded-sm border border-input bg-background px-2 text-sm"
        >
          <option value="">{t('dependency.pickPlaceholder')}</option>
          {candidates.map((row) => (
            <option key={row.id} value={row.id}>
              {row.key} · {row.title}
            </option>
          ))}
        </select>

        <button
          type="button"
          disabled={picked === '' || add.isPending}
          onClick={() => add.mutate(picked)}
          className="inline-flex items-center gap-1 text-xs text-primary disabled:opacity-40"
        >
          <Plus className="size-4" aria-hidden />
          {t('dependency.add')}
        </button>
      </div>
    </section>
  );
}
